import type { MutableRefObject, ReactElement } from 'react';
import { useRef } from 'react';
import type { AudienceSegment, DocumentSection, PlanPath, Proposal } from '@shared/types';
import type { PageActions } from '../types';
import { EditableMoney, EditableText } from '../../components/Editable';
import { count, money } from '../../lib/format';
import { channel, forecastUnavailable, pricedLine, tacticDeliveryMetrics } from '../../lib/plan';
import { useBindings } from '../../state/BindingsContext';
import { fitScaleStyle, useFitScale, useSharedFitScale } from '../../hooks/useFitScale';
import { StatTile, TagPill } from './primitives';
import {
  BriefcaseIcon,
  CalendarIcon,
  CartIcon,
  InfoIcon,
  MegaphoneIcon,
  PeopleIcon,
  PhoneIcon,
  RefreshIcon,
  StoreIcon,
  TicketIcon,
  landingExperienceIcon,
} from './icons';

/**
 * product_plan `audience_plan` variant (`docs/momentum-v3-slides.md`
 * product_plan; media-pairs-all-contract.md "Details slide: audience_plan").
 * Renders for social / targeted_display / mobile_location / email — two
 * audience lanes (prospecting + retargeting, typically) plus a metric rail,
 * same skeleton as `dual_path` (`ProductPlanDualPath.tsx`), which this file
 * deliberately does not import from or edit: a concurrent stream owns that
 * file and `momentum-v3-mediapair.css`'s geometry/spacing/colour tokens, so
 * this component instead REUSES those already-landed `.mv3-dualpath-*` /
 * `.mv3-planpath-*` classes as-is (same visual system, zero risk of a merge
 * collision) and only introduces new `.mv3-planvariant-*` classes (this
 * file's own stylesheet, `momentum-v3-planvariants.css`) for the pieces
 * dual_path has no equivalent of: the rail's "THE MESSAGE" card body/format
 * lines and the lane heading's megaphone/refresh default icons.
 *
 * Differences from dual_path (media-pairs-all-contract.md):
 * - chips block is "PLACEMENTS" (`paths[i].placements`), not "EXAMPLE
 *   SEARCHES" (`searchThemes`, which the agent contract leaves empty here).
 * - lane heading icon defaults by index (0 -> megaphone/prospecting, 1 ->
 *   refresh/retargeting) instead of ticket/briefcase; `path.icon` still
 *   overrides when the agent sets one from the existing enum.
 * - `content.creative` renders as its OWN rail card (megaphone icon,
 *   tracked "THE MESSAGE" title, the message at 22px bold, then the format
 *   as a small tracked line) between the metric tile(s) and the callout —
 *   never inside a lane card and never as a header strip. Fix round (real
 *   ad6abc03 deck): the header-strip-that-folds-into-card-1 design measured
 *   lane cards at fit scale ~0.73-0.76 on that deck because the folded
 *   message made card 1's content taller than the row while the rail sat
 *   nearly empty (callout ~370px in a ~418px box) — moving "THE MESSAGE"
 *   into the rail instead (which has the spare height) lets the lane cards
 *   go back to dual_path's own density and floor (0.78).
 * - the metric rail switches on the tactic's OWN pricing kind (cpm/cpc/flat)
 *   instead of dual_path's fixed cpc-only rail.
 * - the callout icon is refresh (retargeting) or info (note), never shield.
 *
 * `content.body` / `fields.measurementNote` are never read here — the
 * landed agent contract (`src/agent/prompt.ts`) tells the agent not to set
 * either on this variant; any stale value from an older run is intentionally
 * ignored rather than surfaced.
 *
 * Fit-scale: the two lane cards share ONE scale (`useSharedFitScale`, same
 * 0.78 floor as dual_path — no fold logic left to justify a lower one) so
 * side-by-side type always matches; the rail's message card and callout
 * each get their OWN independent `useFitScale` (same mechanism dual_path's
 * callout uses, different floors — see `messageScale`'s own doc below) and
 * split the rail column's remaining height below the metric tile(s) 50/50,
 * fixed (`.mv3-dualpath-rail > .mv3-dualpath-callout { flex: 1 1 0% }` in
 * momentum-v3-planvariants.css). That fixed split matters, not just the
 * numbers: two independent `useFitScale` calls on flex SIBLINGS whose
 * `flex-basis` is `auto` (the reused class's own default) feed back into
 * each other — each hook's chosen scale changes `fitScaleStyle`'s
 * compensating width, which changes that card's auto content height, which
 * changes its share of the row, which changes the available height the
 * OTHER hook is measuring against — and on the real ad6abc03 fixture that
 * never settles at zero overflow, only at a stable ~19px-over state.
 * Pinning both cards' `flex-basis` to 0% fixes each one's available height
 * before either hook runs, independent of what scale either picks, which
 * breaks the loop. Nothing is ever clipped or ellipsized; over-long content
 * only ever shrinks (floored) via these hooks.
 */

type LaneIconKind = NonNullable<PlanPath['icon']>;

const LANE_ICONS: Record<LaneIconKind, (props: { size?: number }) => ReactElement> = {
  ticket: TicketIcon,
  briefcase: BriefcaseIcon,
  calendar: CalendarIcon,
  cart: CartIcon,
  phone: PhoneIcon,
  store: StoreIcon,
};

/** Lane heading icon: `path.icon` when the agent set one, else megaphone (index 0, prospecting) / refresh (index 1, retargeting). */
function laneIcon(icon: PlanPath['icon'], index: number, size: number): ReactElement {
  if (icon) {
    const Icon = LANE_ICONS[icon];
    return <Icon size={size} />;
  }
  return index === 0 ? <MegaphoneIcon size={size} /> : <RefreshIcon size={size} />;
}

function segmentNames(proposal: Proposal, ids: string[]): string[] {
  const byId = new Map(proposal.audience.segments.map((segment) => [segment.id, segment]));
  return ids
    .map((id) => byId.get(id))
    .filter((segment): segment is AudienceSegment => !!segment)
    .map((segment) => segment.name || segment.segment || '')
    .filter((name) => name.length > 0)
    .slice(0, 3);
}

function replacePath(paths: PlanPath[], index: number, patch: Partial<PlanPath>): PlanPath[] {
  return paths.map((path, i) => (i === index ? { ...path, ...patch } : path));
}

function LaneCard({
  path,
  index,
  paths,
  proposal,
  section,
  actions,
  readOnly,
  innerRef,
  scale,
}: {
  path: PlanPath;
  index: number;
  paths: PlanPath[];
  proposal: Proposal;
  section: DocumentSection;
  actions: PageActions;
  readOnly: boolean;
  innerRef: MutableRefObject<HTMLDivElement | null>;
  scale: number;
}): ReactElement {
  const resolve = useBindings();
  const audienceNames = segmentNames(proposal, path.audienceSegmentIds);
  const placements = (path.placements ?? []).slice(0, 4);

  return (
    <div className="mv3-planpath-card">
      <div className="mv3-planpath-card-inner" ref={innerRef} style={fitScaleStyle(scale)}>
        <div className="mv3-planpath-heading">
          <span className="mv3-planpath-icon">{laneIcon(path.icon, index, 48)}</span>
          <div className="mv3-planpath-heading-text">
            <div className="mv3-planpath-label">
              <EditableText
                value={path.label}
                field="headline"
                placeholder="Lane label"
                readOnly={readOnly}
                variant="headline"
                onSave={(next) => actions.saveContent(section, { paths: replacePath(paths, index, { label: next }) })}
              />
            </div>
            <div className="mv3-planpath-role">
              <EditableText
                value={path.role}
                field="body"
                placeholder="What this lane does"
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { paths: replacePath(paths, index, { role: next }) })}
              />
            </div>
          </div>
        </div>

        {placements.length > 0 ? (
          <div className="mv3-planpath-block">
            <div className="mv3-planpath-eyebrow">Placements</div>
            <div className="mv3-planpath-chips">
              {placements.map((placement) => (
                <TagPill tone="ghost" key={placement}>
                  {resolve(placement)}
                </TagPill>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mv3-planpath-divider" />

        {/* Fixed 96px icon column shared with the heading circle above, so
            the tracked label sits directly over its own content — same
            `.mv3-planpath-block-icon` pattern dual_path's PathCard uses
            (alignment fix round 5; the old `mv3-planpath-eyebrow-row`
            offset the label beside the icon instead and is gone). */}
        <div className="mv3-planpath-block mv3-planpath-block-icon">
          <span className="mv3-planpath-icon-col">{landingExperienceIcon(index, { size: 72 })}</span>
          <div className="mv3-planpath-content-col">
            <div className="mv3-planpath-eyebrow">Landing experience</div>
            <div className="mv3-planpath-landing">
              <span>{resolve(path.landingLabel)}</span>
              <span className="mv3-planpath-arrow" aria-hidden="true">
                →
              </span>
              <span>{resolve(path.conversionLabel)}</span>
            </div>
          </div>
        </div>

        {audienceNames.length > 0 ? (
          <>
            <div className="mv3-planpath-divider" />
            <div className="mv3-planpath-block mv3-planpath-block-icon">
              <span className="mv3-planpath-icon-col">
                <PeopleIcon size={64} />
              </span>
              <div className="mv3-planpath-content-col">
                <div className="mv3-planpath-eyebrow">Priority audiences</div>
                <div className="mv3-planpath-chips">
                  {audienceNames.map((name) => (
                    <TagPill tone="ghost" key={name}>
                      {resolve(name)}
                    </TagPill>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export function AudiencePlan({
  proposal,
  section,
  actions,
  readOnly,
  label,
}: {
  proposal: Proposal;
  section: DocumentSection;
  actions: PageActions;
  readOnly: boolean;
  label: string;
}): ReactElement {
  const resolve = useBindings();
  const productId = section.productId;
  const content = section.content;
  const paths = (content.paths ?? []).slice(0, 2);
  const line = productId ? channel(proposal, productId) : null;
  const metrics = productId ? tacticDeliveryMetrics(proposal, productId) : null;
  const priced = productId ? pricedLine(proposal, productId) : null;
  const conversions = priced?.estConversionsMonthly ?? null;
  const headline = content.headline || label;
  const callout = content.callout ?? null;
  const creative = content.creative ?? null;
  const budgetDisplay = line ? money(line.monthlyBudget) : null;

  const calloutInnerRef = useRef<HTMLDivElement | null>(null);
  const calloutScale = useFitScale(calloutInnerRef, { min: 0.55 });

  // The rail's "THE MESSAGE" card gets its own independent fit scale, same
  // mechanism as the callout above but a lower floor: this card carries one
  // more block (icon + title + body + format line, vs the callout's icon +
  // title + body) inside an equal, fixed 50/50 split of the same row (see
  // `.mv3-dualpath-rail > .mv3-dualpath-callout` in
  // momentum-v3-planvariants.css), so its worst case needs more room than
  // dual_path's callout floor (0.78) was ever calibrated against — same
  // reasoning as the lane cards' own floor before the fold was removed. On
  // the real ad6abc03 fixture the longer of the two messages ("Real laughs
  // from a real room — see who's on stage this weekend.") needs ~0.6
  // natural; 0.55 keeps a small margin, same margin the previous lane-card
  // floor used for its own worst case.
  const messageInnerRef = useRef<HTMLDivElement | null>(null);
  const messageScale = useFitScale(messageInnerRef, { min: 0.55 });

  // Two lanes always (media-pairs-all-contract.md: "exactly 2 lanes"), same
  // shared-scale pattern (and the same 0.78 floor) as dual_path's two path
  // cards — now that "THE MESSAGE" always renders in the rail (never folded
  // into card 1), a lane card's worst case is identical to dual_path's own
  // (PLACEMENTS + LANDING EXPERIENCE + PRIORITY AUDIENCES), so dual_path's
  // floor applies unchanged.
  const laneInnerRef0 = useRef<HTMLDivElement | null>(null);
  const laneInnerRef1 = useRef<HTMLDivElement | null>(null);
  const laneScale = useSharedFitScale(laneInnerRef0, laneInnerRef1, { min: 0.78 });

  const calloutIcon = callout?.kind === 'note' ? <InfoIcon size={36} /> : <RefreshIcon size={36} />;
  const calloutDefaultTitle = callout?.kind === 'note' ? 'Note' : 'Retargeting';

  return (
    <div className="mv3-dualpath-frame">
      <div className="mv3-dualpath-header">
        <div className="mv3-eyebrow">{label}</div>
        <div className="mv3-dualpath-headline">
          <EditableText
            value={headline}
            field="headline"
            placeholder={label}
            readOnly={readOnly}
            variant="headline"
            onSave={(next) => actions.saveContent(section, { headline: next })}
          />
        </div>
        {productId && line ? (
          <div className="mv3-dualpath-budget">
            <span className="mv3-dualpath-budget-label">Monthly budget</span>
            <span className="mv3-dualpath-budget-value">
              <EditableMoney
                value={line.monthlyBudget}
                display={budgetDisplay ? `${budgetDisplay} / month` : null}
                readOnly={readOnly}
                label={`${label} monthly budget`}
                onSave={(next) => actions.setChannelBudget(productId, next, label)}
              />
            </span>
          </div>
        ) : null}
      </div>

      <div className="mv3-dualpath-content">
        <div className="mv3-dualpath-cards">
          {paths.map((path, index) => (
            <LaneCard
              key={path.id}
              path={path}
              index={index}
              paths={paths}
              proposal={proposal}
              section={section}
              actions={actions}
              readOnly={readOnly}
              innerRef={index === 0 ? laneInnerRef0 : laneInnerRef1}
              scale={laneScale}
            />
          ))}
        </div>
        <div className="mv3-dualpath-rail">
          {metrics?.kind === 'cpm' ? (
            <StatTile className="mv3-dualpath-tile" label="Est. impressions / month" value={count(metrics.impressions)} />
          ) : null}
          {metrics?.kind === 'cpc' ? (
            <>
              <StatTile className="mv3-dualpath-tile" label="Est. clicks / month" value={count(metrics.clicks)} />
              {conversions !== null ? (
                <StatTile className="mv3-dualpath-tile" label="Est. conversions / month" value={count(conversions)} />
              ) : null}
            </>
          ) : null}
          {metrics?.kind === 'flat' ? (
            <>
              <StatTile
                className="mv3-dualpath-tile"
                label={metrics.unitLabel ? `Number of ${metrics.unitLabel}` : 'Units'}
                value={count(metrics.units)}
              />
              <StatTile className="mv3-dualpath-tile" label="Drop date" value={metrics.dateLabel} />
            </>
          ) : null}
          {/* F13: a planned channel whose forecast came back entirely empty
              (e.g. meta_advertising on c5b885d3) used to leave this rail
              blank — no tile at all, indistinguishable from still loading. */}
          {productId && metrics && forecastUnavailable(proposal, productId, metrics) ? (
            <StatTile className="mv3-dualpath-tile" label="Est. delivery / month" value="No forecast yet" />
          ) : null}
          {creative?.message ? (
            <div className="mv3-dualpath-callout">
              <div className="mv3-dualpath-callout-inner" ref={messageInnerRef} style={fitScaleStyle(messageScale)}>
                <span className="mv3-dualpath-callout-icon">
                  <MegaphoneIcon size={36} />
                </span>
                <div className="mv3-dualpath-callout-title">The message</div>
                <div className="mv3-planvariant-message-body">
                  <EditableText
                    value={creative.message}
                    field="body"
                    placeholder="The on-screen line the ad carries"
                    readOnly={readOnly}
                    onSave={(next) => actions.saveContent(section, { creative: { ...creative, message: next } })}
                  />
                </div>
                {creative.format ? (
                  <div className="mv3-planvariant-message-formatline">
                    Format ·{' '}
                    <EditableText
                      value={creative.format}
                      field="body"
                      placeholder="Format"
                      readOnly={readOnly}
                      onSave={(next) => actions.saveContent(section, { creative: { ...creative, format: next } })}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          {callout ? (
            <div className="mv3-dualpath-callout">
              <div className="mv3-dualpath-callout-inner" ref={calloutInnerRef} style={fitScaleStyle(calloutScale)}>
                <span className="mv3-dualpath-callout-icon">{calloutIcon}</span>
                <div className="mv3-dualpath-callout-title">{callout.title ? resolve(callout.title) : calloutDefaultTitle}</div>
                <div className="mv3-dualpath-callout-body">
                  <EditableText
                    value={callout.body}
                    field="body"
                    placeholder="Callout body"
                    readOnly={readOnly}
                    onSave={(next) => actions.saveContent(section, { callout: { ...callout, body: next } })}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
